import { Percent, X, TrendingUp, TrendingDown, Tag } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

import { cn } from '@/lib/utils';
import type { ICompanyItemInterface } from '../../api/CompanyItems.interface';

type Props = {
  minDiscount: number | '';
  maxDiscount: number | '';
  setMinDiscount: (value: number | '') => void;
  setMaxDiscount: (value: number | '') => void;
  filterData: ICompanyItemInterface[] | null;
};

const DiscountFilter = ({
  minDiscount,
  maxDiscount,
  setMinDiscount,
  setMaxDiscount,
  filterData,
}: Props) => {
  const minValue =
    filterData && filterData.length > 0
      ? Math.min(...filterData.map((item) => item?.Discount || 0))
      : undefined;

  const maxValue =
    filterData && filterData.length > 0
      ? Math.max(...filterData.map((item) => item?.Discount || 0))
      : undefined;

  const avgValue =
    filterData && filterData.length > 0
      ? Math.round(
          filterData.reduce((sum, item) => sum + (item?.Discount || 0), 0) /
            filterData.length,
        )
      : undefined;

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMinDiscount(value ? parseInt(value) : '');
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMaxDiscount(value ? parseInt(value) : '');
  };

  const clearFilters = () => {
    setMinDiscount('');
    setMaxDiscount('');
  };

  const setPreset = (min: number | '', max: number | '') => {
    setMinDiscount(min);
    setMaxDiscount(max);
  };

  const hasFilters = minDiscount !== '' || maxDiscount !== '';

  const getDiscountColor = (discount: number) => {
    if (discount >= 20) return 'text-green-600 dark:text-green-400';
    if (discount >= 10) return 'text-blue-600 dark:text-blue-400';
    if (discount >= 5) return 'text-amber-600 dark:text-amber-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  return (
    <Card className="w-full border-0 bg-gradient-to-br from-muted/30 to-muted/10 p-3">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-primary/10 p-1.5">
              <Tag className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">تصفية الخصم</h3>
              <p className="text-xs text-muted-foreground">
                حدد نطاق نسبة الخصم
              </p>
            </div>
          </div>

          {hasFilters && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 hover:bg-destructive/10 hover:text-destructive"
              onClick={clearFilters}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        {minValue !== undefined && maxValue !== undefined && (
          <div className="grid grid-cols-3 gap-2 rounded-lg bg-muted/20 p-2">
            <div className="text-center">
              <p className="mb-0.5 text-[10px] text-muted-foreground">
                الحد الأدنى
              </p>
              <Badge variant="outline" className="bg-background/50 text-xs">
                <TrendingDown className="ml-1 h-3 w-3" />
                <span className={getDiscountColor(minValue)}>{minValue}%</span>
              </Badge>
            </div>
            <div className="text-center">
              <p className="mb-0.5 text-[10px] text-muted-foreground">
                المتوسط
              </p>
              <Badge variant="outline" className="bg-background/50 text-xs">
                <span className={getDiscountColor(avgValue || 0)}>
                  {avgValue}%
                </span>
              </Badge>
            </div>
            <div className="text-center">
              <p className="mb-0.5 text-[10px] text-muted-foreground">
                الحد الأقصى
              </p>
              <Badge variant="outline" className="bg-background/50 text-xs">
                <TrendingUp className="ml-1 h-3 w-3" />
                <span className={getDiscountColor(maxValue)}>{maxValue}%</span>
              </Badge>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">من</label>
              <div className="relative">
                <Input
                  type="number"
                  placeholder={
                    minValue !== undefined ? `${minValue}%` : 'الحد الأدنى'
                  }
                  value={minDiscount}
                  onChange={handleMinChange}
                  min="0"
                  max="100"
                  className={cn(
                    'h-9 text-sm border-border/50 bg-background/50 pl-8 pr-3 transition-colors focus:bg-background',
                    minDiscount !== '' && 'border-primary/30 bg-primary/5',
                  )}
                />
                <Percent className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">إلى</label>
              <div className="relative">
                <Input
                  type="number"
                  placeholder={
                    maxValue !== undefined ? `${maxValue}%` : 'الحد الأقصى'
                  }
                  value={maxDiscount}
                  onChange={handleMaxChange}
                  min="0"
                  max="100"
                  className={cn(
                    'h-9 text-sm border-border/50 bg-background/50 pl-8 pr-3 transition-colors focus:bg-background',
                    maxDiscount !== '' && 'border-primary/30 bg-primary/5',
                  )}
                />
                <Percent className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
              </div>
            </div>
          </div>

          {minValue !== undefined && maxValue !== undefined && (
            <div className="grid grid-cols-2 gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreset('', 5)}
                className="h-7 text-xs hover:border-primary/30 hover:bg-primary/10 hover:text-primary"
              >
                0-5%
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreset(5, 10)}
                className="h-7 text-xs hover:border-primary/30 hover:bg-primary/10 hover:text-primary"
              >
                5-10%
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreset(10, 20)}
                className="h-7 text-xs hover:border-primary/30 hover:bg-primary/10 hover:text-primary"
              >
                10-20%
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreset(20, '')}
                className="h-7 text-xs hover:border-primary/30 hover:bg-primary/10 hover:text-primary"
              >
                +20%
              </Button>
            </div>
          )}
        </div>

        {hasFilters && (
          <div className="border-t border-border/30 pt-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">نطاق الخصم:</span>
              <div className="flex items-center gap-2">
                {minDiscount !== '' && (
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-xs',
                      'border-primary/20 bg-primary/5',
                      getDiscountColor(minDiscount),
                    )}
                  >
                    من {minDiscount}%
                  </Badge>
                )}
                {maxDiscount !== '' && (
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-xs',
                      'border-primary/20 bg-primary/5',
                      getDiscountColor(maxDiscount),
                    )}
                  >
                    إلى {maxDiscount}%
                  </Badge>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default DiscountFilter;
